import type { AttrValue, NodeId, NodeSnapshot, Op, PermissionEntry, TreeEvent } from './types.js';

export interface TreeNode {
  readonly id: NodeId;
  readonly name: string;
  parent: TreeNode | null;
  attrs: Map<string, AttrValue>;
  perms: PermissionEntry[];
  children: TreeNode[];
}

/**
 * Nomes de atributo e de nó filho comparados como no NDB do original: sem
 * diferenciar maiúsculas nem acentos (TUnicode::Compare com IgnoreCase e
 * IgnoreDiacritics). Plugins escrevem "PVMax" e leem "PVmax" e funciona.
 */
export function foldName(name: string): string {
  return name.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** A chave já gravada no nó para esse nome (a primeira grafia vale), ou o próprio nome. */
export function attrKey(node: TreeNode, name: string): string {
  if (node.attrs.has(name)) return name;
  const folded = foldName(name);
  for (const key of node.attrs.keys()) if (foldName(key) === folded) return key;
  return name;
}

export function getAttr(node: TreeNode | undefined, name: string): AttrValue | undefined {
  return node ? node.attrs.get(attrKey(node, name)) : undefined;
}

/**
 * Bytes que o texto ocupa no JSON gravado (UTF-8, com as aspas e os escapes), arredondado para
 * cima: o teto do documento no servidor (`DocumentLimits`) é medido assim.
 */
export function textBytes(s: string): number {
  let n = s.length + 2;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c >= 0x800) n += 2;
    else if (c >= 0x80) n += 1;
    else if (c < 0x20) n += 5;
    else if (c === 0x22 || c === 0x5c) n += 1;
  }
  return n;
}

/** `{"id":…,"name":…,"attrs":{},"perms":[],"children":[]},` sem o id e o nome */
const NODE_BYTES = 52;

/** O que um atributo acrescenta ao JSON do nó: `"nome":valor,`. */
export function attrBytes(name: string, value: AttrValue): number {
  return textBytes(name) + 2 + (typeof value === 'string' ? textBytes(value) : String(value).length);
}

/** O que uma permissão acrescenta: `{"kind":"group","id":…,"permission":…,"allowance":…},`. */
export function permBytes(p: Pick<PermissionEntry, 'id' | 'permission' | 'allowance'>): number {
  return 50 + textBytes(p.id) + textBytes(p.permission) + textBytes(p.allowance);
}

/** O nó vazio (sem atributos, permissões nem filhos). */
export function nodeBytes(id: NodeId, name: string): number {
  return NODE_BYTES + textBytes(id) + textBytes(name);
}

function subtreeBytes(n: TreeNode): number {
  let total = nodeBytes(n.id, n.name);
  for (const [k, v] of n.attrs) total += attrBytes(k, v);
  for (const p of n.perms) total += permBytes(p);
  for (const c of n.children) total += subtreeBytes(c);
  return total;
}

export class OpError extends Error {
  constructor(message: string, readonly op: Op) {
    super(message);
  }
}

/**
 * Plain in-memory tree. Applies ops deterministically and reports what changed.
 * Has no notion of users or replication — see Replica and NdbDocument.
 */
export class NodeTree {
  readonly root: TreeNode;
  private readonly index = new Map<NodeId, TreeNode>();
  private total: number;

  constructor(rootId: NodeId = 'root', rootName = 'root') {
    this.root = { id: rootId, name: rootName, parent: null, attrs: new Map(), perms: [], children: [] };
    this.index.set(rootId, this.root);
    this.total = nodeBytes(rootId, rootName);
  }

  static fromSnapshot(s: NodeSnapshot): NodeTree {
    const t = new NodeTree(s.id, s.name);
    t.load(t.root, s);
    t.total = subtreeBytes(t.root);
    return t;
  }

  /** O tamanho do snapshot em JSON, em bytes (`textBytes`), mantido a cada op. */
  get bytes(): number {
    return this.total;
  }

  /** Quantos nós a árvore tem, com a raiz. */
  get nodeCount(): number {
    return this.index.size;
  }

  private load(target: TreeNode, s: NodeSnapshot): void {
    target.attrs = new Map(Object.entries(s.attrs));
    target.perms = s.perms.map((p) => ({ ...p }));
    for (const c of s.children) {
      const child: TreeNode = { id: c.id, name: c.name, parent: target, attrs: new Map(), perms: [], children: [] };
      this.index.set(c.id, child);
      target.children.push(child);
      this.load(child, c);
    }
  }

  snapshot(node: TreeNode = this.root): NodeSnapshot {
    return {
      id: node.id,
      name: node.name,
      attrs: Object.fromEntries(node.attrs),
      perms: node.perms.map((p) => ({ ...p })),
      children: node.children.map((c) => this.snapshot(c)),
    };
  }

  clone(): NodeTree {
    return NodeTree.fromSnapshot(this.snapshot());
  }

  get(id: NodeId): TreeNode | undefined {
    return this.index.get(id);
  }

  has(id: NodeId): boolean {
    return this.index.has(id);
  }

  findChild(id: NodeId, name: string): TreeNode | undefined {
    const children = this.index.get(id)?.children;
    if (!children) return undefined;
    const exact = children.find((c) => c.name === name);
    if (exact) return exact;
    const folded = foldName(name);
    return children.find((c) => foldName(c.name) === folded);
  }

  /** Path of ancestors from the node up to the root, node first. */
  ancestry(id: NodeId): TreeNode[] {
    const out: TreeNode[] = [];
    for (let n = this.index.get(id); n; n = n.parent ?? undefined) out.push(n);
    return out;
  }

  isAncestor(ancestor: NodeId, id: NodeId): boolean {
    return this.ancestry(id).some((n) => n.id === ancestor);
  }

  /** Apply one op. Throws OpError when it cannot apply (unknown node, duplicate id, …). */
  apply(op: Op): TreeEvent[] {
    switch (op.t) {
      case 'set': {
        const n = this.need(op.node, op);
        const key = attrKey(n, op.name);
        const old = n.attrs.get(key);
        if (op.value === null) {
          if (old === undefined) return [];
          n.attrs.delete(key);
          this.total -= attrBytes(key, old);
        } else {
          if (old === op.value) return [];
          n.attrs.set(key, op.value);
          this.total += attrBytes(key, op.value) - (old === undefined ? 0 : attrBytes(key, old));
        }
        return [{ type: 'changed', node: n.id, attribute: key, oldValue: old, newValue: op.value ?? undefined }];
      }
      case 'add': {
        const p = this.need(op.parent, op);
        if (this.index.has(op.id)) throw new OpError(`node ${op.id} already exists`, op);
        const child: TreeNode = { id: op.id, name: op.name, parent: p, attrs: new Map(), perms: [], children: [] };
        const at = op.index === undefined ? p.children.length : Math.max(0, Math.min(op.index, p.children.length));
        p.children.splice(at, 0, child);
        this.index.set(child.id, child);
        this.total += nodeBytes(child.id, child.name);
        return [{ type: 'childAdded', parent: p.id, child: child.id }];
      }
      case 'del': {
        const n = this.need(op.node, op);
        if (!n.parent) throw new OpError('cannot delete the root', op);
        return this.detach(n);
      }
      case 'clear': {
        const n = this.need(op.node, op);
        const ev: TreeEvent[] = [];
        for (const c of [...n.children]) ev.push(...this.detach(c));
        for (const [k, v] of n.attrs) {
          ev.push({ type: 'changed', node: n.id, attribute: k, oldValue: v, newValue: undefined });
          this.total -= attrBytes(k, v);
        }
        n.attrs.clear();
        return ev;
      }
      case 'rmChildren': {
        const n = this.need(op.node, op);
        const folded = foldName(op.name);
        return n.children.filter((c) => foldName(c.name) === folded).flatMap((c) => this.detach(c));
      }
      case 'move': {
        const n = this.need(op.node, op);
        const parent = n.parent;
        if (!parent) throw new OpError('cannot move the root', op);
        const from = parent.children.indexOf(n);
        const to = Math.max(0, Math.min(op.index, parent.children.length - 1));
        if (from === to) return [];
        parent.children.splice(from, 1);
        parent.children.splice(to, 0, n);
        // um reordenamento: sai e volta no mesmo pai, sem apagar nada
        return [
          { type: 'childRemoved', parent: parent.id, child: n.id },
          { type: 'childAdded', parent: parent.id, child: n.id },
        ];
      }
      case 'perm': {
        const n = this.need(op.node, op);
        const i = n.perms.findIndex((p) => p.kind === op.kind && p.id === op.id && p.permission === op.permission);
        const old = i >= 0 ? n.perms[i]! : undefined;
        if (op.allowance === null) {
          if (!old) return [];
          n.perms.splice(i, 1);
        } else if (old) {
          if (old.allowance === op.allowance) return [];
          n.perms[i] = { kind: op.kind, id: op.id, permission: op.permission, allowance: op.allowance };
        } else {
          n.perms.push({ kind: op.kind, id: op.id, permission: op.permission, allowance: op.allowance });
        }
        this.total += (op.allowance === null ? 0 : permBytes({ id: op.id, permission: op.permission, allowance: op.allowance })) - (old ? permBytes(old) : 0);
        return [{ type: 'permissionsChanged', node: n.id }];
      }
      case 'resetPerms': {
        const n = this.need(op.node, op);
        if (n.perms.length === 0) return [];
        for (const p of n.perms) this.total -= permBytes(p);
        n.perms = [];
        return [{ type: 'permissionsChanged', node: n.id }];
      }
    }
  }

  private need(id: NodeId, op: Op): TreeNode {
    const n = this.index.get(id);
    if (!n) throw new OpError(`unknown node ${id}`, op);
    return n;
  }

  private detach(n: TreeNode): TreeEvent[] {
    const parent = n.parent!;
    this.total -= subtreeBytes(n);
    parent.children.splice(parent.children.indexOf(n), 1);
    const ev: TreeEvent[] = [];
    const drop = (x: TreeNode) => {
      for (const c of x.children) drop(c);
      this.index.delete(x.id);
      ev.push({ type: 'deleted', node: x.id });
    };
    drop(n);
    n.parent = null;
    ev.push({ type: 'childRemoved', parent: parent.id, child: n.id });
    return ev;
  }
}

/** Events that turn tree `a` into tree `b` (used after a rollback/rebase). */
export function diffTrees(a: NodeTree, b: NodeTree): TreeEvent[] {
  const ev: TreeEvent[] = [];
  const walk = (x: TreeNode | undefined, y: TreeNode) => {
    const xa = x?.attrs ?? new Map<string, AttrValue>();
    for (const [k, v] of y.attrs) {
      if (xa.get(k) !== v) ev.push({ type: 'changed', node: y.id, attribute: k, oldValue: xa.get(k), newValue: v });
    }
    for (const [k, v] of xa) {
      if (!y.attrs.has(k)) ev.push({ type: 'changed', node: y.id, attribute: k, oldValue: v, newValue: undefined });
    }
    if (x && JSON.stringify(x.perms) !== JSON.stringify(y.perms)) ev.push({ type: 'permissionsChanged', node: y.id });
    const xIds = new Set(x?.children.map((c) => c.id));
    const yIds = new Set(y.children.map((c) => c.id));
    for (const c of x?.children ?? []) {
      if (!yIds.has(c.id)) {
        const dropped: TreeEvent[] = [];
        const drop = (n: TreeNode) => {
          n.children.forEach(drop);
          dropped.push({ type: 'deleted', node: n.id });
        };
        drop(c);
        ev.push(...dropped, { type: 'childRemoved', parent: y.id, child: c.id });
      }
    }
    for (const c of y.children) {
      if (!xIds.has(c.id)) ev.push({ type: 'childAdded', parent: y.id, child: c.id });
      walk(x?.children.find((xc) => xc.id === c.id), c);
    }
  };
  walk(a.root, b.root);
  return ev;
}
